import { format } from 'date-fns';
import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../../constant/Events';
import { IconSize } from '../../../constant/IconSize.constant';
import { SUPPORT_EMAIL } from '../../../constant/Text.constant';
import { useFetchStatisticsQuery } from '../../../services/history.api';
import { useLazyExportPayoutReportUserTransactionPDFQuery } from '../../../services/payouts.api';
import { Statistics } from '../../../stores/types';
import { convertToLocaleCurrency } from '../../../utils/Currency.Util';
import { convertToDate, formatDate } from '../../../utils/Date.Util';
import { Download } from '../../../assets/icons';
import { displayEneryKwh } from '../../../utils/Number.Util';
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  getHexColorByType,
  Grid,
  Label,
  LabelType,
} from '../../_ui';
import { WarningMessage } from '../../_ui/WarningMessage';
import { useCompany } from '../../../hooks/useCompany';
import { useAuth, USER_ROLE } from '../../../hooks';
import { SCOPE_TYPE } from '../../../services/utils';

const PayoutHistory = memo(() => {
  const { t } = useTranslation();
  const { companyId } = useCompany();

  // constants
  const today = new Date();

  // API
  const sortStats = (data: any) => {
    const sortedStats = [...data];
    return sortedStats.sort((a: any, b: any) => (new Date(a.date) < new Date(b.date) ? 1 : -1));
  };
  const { stats, isStatsLoading } = useFetchStatisticsQuery(
    {
      toDate: formatDate(today, 'yyyy-MM'),
      currency: 'USD',
      aggregateLocations: true,
    },
    {
      selectFromResult: (endpoint) => ({
        stats: endpoint.data
          ? sortStats(endpoint.data).map((obj: Statistics, index: number) => {
              return {
                id: index,
                formatedDate: formatDate(convertToDate(obj.date), 'LLL yyyy'),
                ...obj,
              };
            })
          : [],
        isStatsLoading: endpoint.isFetching,
      }),
    },
  );
  const [triggerExportUserTransactionReportPDF] =
    useLazyExportPayoutReportUserTransactionPDFQuery();
  const [currentPage, setCurrentPage] = useState(1);
  const auth = useAuth();
  const handleDownloadClick = (row: any) => {
    captureAnalyticsEvent(AnalyticsEvent.PAYOUT_EXPORT_EVENT);
    const yearMonth = format(convertToDate(row.date), 'yyyyMM');
    const scope = auth.role === USER_ROLE.SUPPORT ? SCOPE_TYPE.ALL : SCOPE_TYPE.COMPANY;
    triggerExportUserTransactionReportPDF({
      companyId: companyId,
      yearMonth,
      scope,
    });
  };

  const columnsSettings = [
    {
      key: 'formatedDate',
      title: t('billing_cycle'),
    },
    {
      key: 'energyDeliveredKWh',
      title: t('overivew_energy_used'),
      component: (row: any) => (
        <Label
          text={
            row.energyDeliveredKWh || row.energyDeliveredKWh === 0
              ? displayEneryKwh(row.energyDeliveredKWh, t('kwh'))
              : ''
          }
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          isLoading={isStatsLoading}
          skeletonHeight='h-5'
          className='w-50'
        />
      ),
    },
    {
      key: 'revenue',
      title: t('net_earnings'),
      component: (row: any) => (
        <Label
          text={
            row.revenue || row.revenue === 0
              ? `${convertToLocaleCurrency(row.revenue, row.revenueCurrency)}`
              : ''
          }
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          isLoading={isStatsLoading}
          skeletonHeight='h-5'
          className='w-20'
        />
      ),
    },
    {
      key: 'download',
      class: 'text-right w-24',
      component: (row: any) => {
        const rowDate = convertToDate(row.date);
        const isValidDate = !Number.isNaN(rowDate.getTime());
        const currentMonthYear = format(today, 'yyyy-MM');
        const rowMonthYear = isValidDate ? format(rowDate, 'yyyy-MM') : '';
        const isCurrentMonth = isValidDate && currentMonthYear === rowMonthYear;
        const shouldShowDownload =
          (row.energyDeliveredKWh > 0 || row.revenue > 0) && !isCurrentMonth;

        if (!shouldShowDownload) {
          return null;
        }

        return (
          <div className='flex'>
            <Button
              size={ButtonSize.MEDIUM_COMPACT}
              labelIconSize={IconSize.SIZE_16x16}
              label={t('download')}
              type={ButtonType.OUTLINE}
              labelType={LabelType.LABEL_S}
              labelColor={ColorType.GREY6}
              iconColor={ColorType.GREY6}
              icon={Download}
              onClick={() => handleDownloadClick(row)}
              dataTestId={`download-${row.id}`}
              isLoading={isStatsLoading}
            />
          </div>
        );
      },
    },
  ];
  const labelText = (
    <Trans i18nKey='payter_history_post_may2025_desc'>
      <a
        target='_blank'
        href={`mailto:${SUPPORT_EMAIL}`}
        rel='noreferrer'
        className={LabelType.LABEL_XS_MEDIUM}
        style={{ color: getHexColorByType(ColorType.DENIM) }}
      >
        This text will be replaced by actual link text during translation
      </a>
    </Trans>
  );
  return (
    <Card>
      <div className='pb-4'>
        <Label
          text={t('payter_history_title')}
          type={LabelType.H4}
          color={ColorType.BLACK}
          dataTestId='payouts-history'
          className='mb-2'
        />
        <Label
          text={t('payter_history_desc')}
          type={LabelType.BODY3}
          color={ColorType.GREY6}
          className='mb-4'
        />
        <div className='mb-2'>
          <WarningMessage
            text={labelText}
            iconSize={IconSize.SIZE_24x24}
            divClassName='items-start'
          />
        </div>
      </div>
      <div className='payout-compact-grid'>
        <Grid
          pageIndex={currentPage}
          loadPage={(page) => setCurrentPage(page)}
          local
          columns={columnsSettings}
          data={stats}
          primaryKey='id'
          totalPage={Math.ceil(stats.length / 12)}
          rowCount={12}
          isLoading={isStatsLoading}
          dataTestId='payout-data'
        />
      </div>
      <style>
        {`
        .payout-compact-grid tbody td
          {
            padding-top: 4px;
            padding-bottom: 4px;
          }
        `}
      </style>
    </Card>
  );
});

export default PayoutHistory;
